import ListItem from "./ListItem.vue";
import { sendRemoveTeethRequest } from "@/api/toothApi";
import {
  updateRaycaster,
  intersectTooth,
  setHighlightedToothColor,
} from "@/components/intersection";
import { highlightOutlines } from "@/components/dicom/slice.js";
import {
  focusTooth,
  unfocusTooth,
} from "@/components/tooth/focus/ToothFocusMode";
import {
  ToothNumberMode,
  initToothNumberControls,
} from "../tooth/number/ToothNumberMode";
import { showToothNumberDialog } from "../tooth/number/ToothNumberDialog.vue";
import { updateDisplayNumberAndName } from "@/components/tooth/teethNaming.js";
import {
  isAnchorDataInitialized,
  generateRootCrownMeshes,
} from "@/components/anchorage/AnchorageCalculation.js";
import {
  fetchJsonObjAsync,
  getMatrix4FromJsonObj,
} from "@/components/ortho/treatment/OrthoTreatment.js";
import {
  getGeometryFromStl,
  getGeometryFromGlb,
  getGlbMesh,
} from "@/components/geometry/geometryLoader.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import * as BufferGeometryUtils from "three/addons/utils/BufferGeometryUtils.js";
import { BufferAttribute } from "three";
import { showLoader, hideLoader } from "@/components/controls/Loader.vue";

const DEFAULT_BONES_TYPE = "default";
const SOLID_BONES_TYPE = "solid";
const FULL_BONES_TYPE = "full";

let mandibleTransform = null;
let mandibleTransformInverted = null;

let faceItem = null;
let faceMesh = null;
let faceOptions = {};

async function fetchMandibleTransform() {
  try {
    let centerMatrix = globalCenterMatrix.clone();
    let invertedCenterMatrix = centerMatrix.clone().invert();

    let ioUpperTransformUrl = `${vueApp.dicomUrl}/crown-segmentation-standalone/upper_transform.json`;
    let ioUpperTransformObj = await fetchJsonObjAsync(ioUpperTransformUrl);
    let ioUpperTransform = getMatrix4FromJsonObj(ioUpperTransformObj);
    let ioUpperTransformInverted = ioUpperTransform
      ? ioUpperTransform.clone().invert()
      : null;

    let ioLowerTransformUrl = `${vueApp.dicomUrl}/crown-segmentation-standalone/lower_transform.json`;
    let ioLowerTransformObj = await fetchJsonObjAsync(ioLowerTransformUrl);
    let ioLowerTransform = getMatrix4FromJsonObj(ioLowerTransformObj);
    let ioLowerTransformInverted = ioLowerTransform
      ? ioLowerTransform.clone().invert()
      : null;

    let upperTransformUrl = `${vueApp.dicomUrl}/crown-composition/upper.transform.json`;
    let upperTransformObj = await fetchJsonObjAsync(upperTransformUrl);
    let upperTransform = getMatrix4FromJsonObj(upperTransformObj);
    let upperTransformInverted = upperTransform
      ? upperTransform.clone().invert()
      : null;

    let lowerTransformUrl = `${vueApp.dicomUrl}/crown-composition/lower.transform.json`;
    let lowerTransformObj = await fetchJsonObjAsync(lowerTransformUrl);
    let lowerTransform = getMatrix4FromJsonObj(lowerTransformObj);
    let lowerTransformInverted = lowerTransform
      ? lowerTransform.clone().invert()
      : null;

    console.log("fetchMandibleTransform", ioUpperTransform, upperTransform);

    if (
      ioUpperTransform &&
      ioLowerTransform &&
      upperTransform &&
      lowerTransform
    ) {
      mandibleTransform = new THREE.Matrix4();

      mandibleTransform.multiplyMatrices(
        invertedCenterMatrix,
        mandibleTransform,
      );

      mandibleTransform.multiplyMatrices(lowerTransform, mandibleTransform);
      mandibleTransform.multiplyMatrices(
        ioLowerTransformInverted,
        mandibleTransform,
      );
      mandibleTransform.multiplyMatrices(ioUpperTransform, mandibleTransform);
      mandibleTransform.multiplyMatrices(
        upperTransformInverted,
        mandibleTransform,
      );

      mandibleTransform.multiplyMatrices(centerMatrix, mandibleTransform);

      mandibleTransformInverted = mandibleTransform.clone().invert();
      vueApp.isOcclusionAvailable = true;
    }
  } catch (error) {
    console.log("fetchMandibleTransform", error);
  }
}

function snapMandibleToOcclusion(value) {
  let lowerItems = vueApp.allTeeth
    .filter((x) => !x.isUpper)
    .concat(vueApp.bones.filter((x) => !x.isMaxilla))
    .concat(vueApp.gingivae.filter((x) => !x.isUpper))
    .concat(vueApp.nerves);

  lowerItems.forEach((model) => {
    if (!model) return;
    model.needUpdateDicom = true;
    let mesh = viewer.get_model_mesh(model.id);
    mesh.applyMatrix4(value ? mandibleTransform : mandibleTransformInverted);

    let linkedMeshes = [mesh.defaultMesh, mesh.originalTreatmentMesh];
    linkedMeshes.forEach((lmesh) => {
      if (lmesh) {
        lmesh.applyMatrix4(
          value ? mandibleTransform : mandibleTransformInverted,
        );
      }
    });
  });

  vueApp.$emit("positionChanged", lowerItems);
  vueApp.$emit("snapToOcclusionChanged", value);
}

async function fillTeethList() {
  const teethList = document.getElementById("teethList");
  teethList.innerHTML = "";
  let allItems = [];

  let faceMeshUrl = await getFaceMeshUrl();
  if (faceMeshUrl) {
    faceItem = {
      name: "face",
      filename: "face_mesh.stl",
      isVisible: false,
      isFace: true,
      isLoaded: false,
      isTextured: true,
      hasTexture: false,
      hasSmooth: false,
      hasWrap: false,
      geomType: "base",
      color: "#d1929d", //vueApp.bonesColor
      url: `${vueApp.stlBaseUrl}/face_mesh.stl`,
      glbSmoothUrl: `${vueApp.stlBaseUrl}/full_face_mesh.glb`, // full_face_mesh
      glbWrapUrl: `${vueApp.stlBaseUrl}/textured_face_mesh.glb`,
      opacity: 0.7, //0.4, 1.0
      getMesh: function () {
        return faceMesh;
      },
    };
    allItems.push(faceItem);
  }

  allItems.push(...vueApp.allItems);
  allItems.forEach((item) => {
    // item.displayName = item.isTooth
    //   ? item.filename.replace(".stl", "")
    //   : item.name.toLowerCase();
    // item.displayNumber = item.number;
    if (item.isTooth) updateDisplayNumberAndName(item);
    else {
      item.displayName = item.name.toLowerCase();
      if (item.isBone && item.isMaxilla) {
        item.displayName = "max. comp.";
      }
    }
    item.isAirway = vueApp.airways && vueApp.airways.includes(item);
    item.isOpacityVisible = false;
    if (!item.isSmoothed) item.isSmoothed = false;
    item.isHovered = false;
    if (!item.isDeleted) item.isDeleted = false;
    item.isFocused = false;
    item.isHidden = false;
    item.isClicked = false;

    if (item.isSingle) {
      item.isVisible = false;
      item.isHidden = true;
      item.opacity = 0;
      var mesh = viewer ? viewer.get_model_mesh(item.id) : null;
      if (mesh && mesh.material) {
        mesh.material.transparent = true;
        mesh.material.opacity = 0;
        //mesh.visible = false;
      }
      return;
    }

    const itemInstance = new Vue({
      ...ListItem,
      propsData: {
        item: item,
      },
    });
    const vueContainer = document.createElement("div");
    teethList.appendChild(vueContainer);
    itemInstance.$mount(vueContainer);
  });
}

async function switchFaceVisibility() {
  console.log("switchFaceVisibility", faceItem.isVisible);

  if (faceItem.isVisible && !faceItem.isLoaded) {
    showLoader(`Preparing data`, `Please wait...`, true);
    try {
      let response = await fetch(faceItem.url);
      if (response.status === 200) {
        let buffer = await response.arrayBuffer();
        let baseGeometry = getGeometryFromStl(buffer);
        baseGeometry = prepareGeometryForViewer(baseGeometry);
        let baseMaterial = new THREE.MeshPhongMaterial({
          color: faceItem.color,
          flatShading: false,
          specular: 0x050505,
          shininess: 100,
          side: THREE.DoubleSide, // THREE.FrontSide THREE.BackSide THREE.DoubleSide
          transparent: true,
          opacity: faceItem.opacity,
          // depthWrite: false,
          // wireframe: true
        });

        faceOptions["base"] = {
          geometry: baseGeometry,
          center: baseGeometry.center,
          baseMaterial: baseMaterial,
          textureMaterial: baseMaterial,
        };

        let gltfLoader = new GLTFLoader();

        response = await fetch(faceItem.glbSmoothUrl);
        if (response.status === 200) {
          buffer = await response.arrayBuffer();
          let result = await gltfLoader.parseAsync(buffer, "");
          let world = result.scene.children[0];
          let smoothMesh = world.children[0];
          let smoothGeometry = smoothMesh.geometry;
          // smoothGeometry.computeBoundingBox();
          // let smoothBox = offsetBox(smoothGeometry.boundingBox);
          // console.log("smoothBox", smoothBox);
          smoothGeometry = prepareGeometryForViewer(smoothGeometry);
          // fixNormals(smoothGeometry);
          let smoothMaterial = prepareFaceTextureMaterial(smoothMesh.material);
          smoothMaterial.side = THREE.FrontSide;
          // THREE.FrontSide THREE.BackSide THREE.DoubleSide
          faceOptions["smooth"] = {
            geometry: smoothGeometry,
            center: smoothGeometry.center,
            textureMaterial: smoothMaterial,
            baseMaterial: baseMaterial,
          };
          faceItem.hasSmooth = true;
          faceItem.hasTexture = true;
        }

        response = await fetch(faceItem.glbWrapUrl);
        if (response.status === 200) {
          buffer = await response.arrayBuffer();
          let result = await gltfLoader.parseAsync(buffer, "");
          let world = result.scene.children[0];
          let wrapMesh = world.children[0];
          let wrapGeometry = wrapMesh.geometry.clone();
          // wrapGeometry.computeBoundingBox();
          // let wrapBox = offsetBox(wrapGeometry.boundingBox);
          // console.log("wrapBox", wrapBox);
          wrapGeometry = prepareGeometryForViewer(wrapGeometry);

          let wrapMaterial = prepareFaceTextureMaterial(wrapMesh.material);
          faceOptions["wrap"] = {
            geometry: wrapGeometry,
            center: wrapGeometry.center,
            textureMaterial: wrapMaterial,
            baseMaterial: baseMaterial,
          };

          faceItem.hasWrap = true;
          faceItem.hasTexture = true;

          let backMesh = world.children[1];
          if (backMesh && backMesh.geometry) {
            let backGeometry = backMesh.geometry;
            const uvs = new Float32Array([]);
            backGeometry.setAttribute("uv", new BufferAttribute(uvs, 2));
            backGeometry.attributes.position.gpuType = 1015;
            baseGeometry = BufferGeometryUtils.mergeGeometries(
              [wrapMesh.geometry, backGeometry],
              false,
            );
            baseGeometry = prepareGeometryForViewer(baseGeometry);
            faceOptions["base"] = {
              geometry: baseGeometry,
              center: baseGeometry.center,
              baseMaterial: baseMaterial,
              textureMaterial: wrapMaterial,
            };
          }
        }

        let faceOption = faceOptions["base"];
        if (faceItem.hasWrap) {
          faceOption = faceOptions["wrap"];
          faceItem.geomType = "wrap";
        } else if (faceItem.hasSmooth) {
          faceOption = faceOptions["smooth"];
          faceItem.geomType = "smooth";
        }


        let faceGeometry = faceOption.geometry;
        let faceMaterial =
          faceItem.hasTexture && faceItem.isTextured
            ? faceOption.textureMaterial
            : faceOption.baseMaterial;

        faceMesh = new THREE.Mesh(faceGeometry, faceMaterial);
        faceMesh.options = faceOptions;
        faceMesh.item = faceItem;
        faceMesh.renderOrder = 2;

        let center = faceGeometry.center;
        faceMesh.position.set(center.x, center.y, center.z);

        viewer.scene.faceMesh = faceMesh;
        viewer.scene.add(faceMesh);

        faceItem.isLoaded = true;
      }
    } catch (error) {
      console.error("faceItem error", error);
    }

    hideLoader();

    if (!faceItem.isLoaded) {
      swal({
        text: "Cannot load face for the case.",
      });
    }
  }
  if (faceMesh) faceMesh.visible = faceItem.isVisible;
}

function prepareFaceTextureMaterial(material) {
  if (material) {
    if (material.map) {
      material.map.colorSpace = "NoColorSpace";
    }
    material.side = THREE.DoubleSide;
    material.transparent = true;
    material.opacity = faceItem.opacity;
  }
  return material;
}

function prepareGeometryForViewer(geometry) {
  //await getGeometryFromGlb(buffer); // getGeometryFromStl(buffer);
  geometry.computeBoundingBox();
  let box = geometry.boundingBox;
  box = rotateBox(box, -defaultXOffset, -defaultYOffset, -defaultZOffset);

  let matrix2 = new THREE.Matrix4();
  matrix2.makeRotationAxis(new THREE.Vector3(0, 1, 0), rotateY);
  geometry.applyMatrix4(matrix2);

  let matrix1 = new THREE.Matrix4();
  matrix1.makeRotationAxis(new THREE.Vector3(1, 0, 0), rotateX);
  geometry.applyMatrix4(matrix1);

  geometry = new THREE.Geometry().fromBufferGeometry(geometry);
  // cgeometry.mergeVertices();
  geometry.computeVertexNormals();
  geometry = new THREE.BufferGeometry().fromGeometry(geometry);
  geometry.computeBoundingBox();

  // box = offsetBox(faceGeometry.boundingBox);
  geometry.baseBox = box;
  geometry.center();

  geometry.center = new THREE.Vector3(box.center.x, box.center.y, box.center.z);

  return geometry;
}

function prepareFullGeometryForViewer(geometry) {
  geometry.computeBoundingBox();
  let box = offsetBox(geometry.boundingBox);
  geometry.baseBox = box;

  geometry.scale(300, 300, 300);
  let matrix2 = new THREE.Matrix4();
  matrix2.makeRotationAxis(new THREE.Vector3(0, 0, 1), rotateY);
  geometry.applyMatrix4(matrix2);

  let matrix1 = new THREE.Matrix4();
  matrix1.makeRotationAxis(new THREE.Vector3(0, 1, 0), rotateY);
  geometry.applyMatrix4(matrix1);

  geometry = new THREE.Geometry().fromBufferGeometry(geometry);
  // cgeometry.mergeVertices();
  geometry.computeVertexNormals();
  geometry = new THREE.BufferGeometry().fromGeometry(geometry);
  geometry.computeBoundingBox();

  // box = offsetBox(faceGeometry.boundingBox);
  geometry.center();
  geometry.center = new THREE.Vector3(0, 15, 30);

  return geometry;
}

function getShaderMaterial(texture) {
  const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_PointSize = 1.0;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

  const fragmentShader = `
  uniform sampler2D map;
  uniform float brightness;
  uniform float saturation;
  varying vec2 vUv;

  void main() {
    vec4 texColor = texture2D(map, vUv);
    // Convert to HSL-like operation: desaturate and brighten
    float gray = dot(texColor.rgb, vec3(0.299, 0.587, 0.114));
    vec3 desaturated = mix(texColor.rgb, vec3(gray), 1.0 - saturation);
    vec3 final = desaturated * brightness;
    gl_FragColor = vec4(final, texColor.a);
  }
`;

  const uniforms = {
    map: { value: texture },
    brightness: { value: 1.0 }, // 1.5 >1 = lighter
    saturation: { value: 1.0 }, // 0.3 0 = grayscale, 1 = original
  };

  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader,
  });
  return material;
}

function lightenTexture(texture, amount = 1.5) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const img = texture.image;

  canvas.width = img.width;
  canvas.height = img.height;

  // Draw original
  ctx.drawImage(img, 0, 0);

  // Overlay a semi-transparent white layer
  ctx.fillStyle = `rgba(255, 255, 255, ${amount - 1})`; // e.g., 0.5 for 50% white overlay
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const newTexture = new THREE.CanvasTexture(canvas);
  return newTexture;
}

function switchFaceMaterial() {
  if (faceItem && faceMesh) {
    switchFaceGeometry(faceItem.geomType);
  }
}

function switchFaceGeometry(geomType) {
  console.log("switchFaceGeometry", geomType);
  if (faceItem && faceMesh) {
    let newOption = faceOptions[geomType];
    if (newOption) {
      let newGeometry = newOption.geometry;
      let center = newOption.center;
      faceMesh.geometry = newGeometry;
      faceMesh.position.set(center.x, center.y, center.z);
      let newMaterial = faceItem.isTextured
        ? newOption.textureMaterial
        : newOption.baseMaterial;
      if (newMaterial) {
        faceMesh.material = newMaterial;
      }
    }
  }
}

async function getFaceMeshUrl() {
  let faceMeshUrl = `${vueApp.stlBaseUrl}/textured_face_mesh.glb`; // textured_face_mesh.glb
  try {
    let response = await fetch(faceMeshUrl, {
      method: "HEAD",
      mode: "cors",
      cache: "no-cache",
    });
    if (response.ok) {
      return faceMeshUrl;
    } else {
      faceMeshUrl = `${vueApp.stlBaseUrl}/face_mesh.stl`;
      response = await fetch(faceMeshUrl, {
        method: "HEAD",
        mode: "cors",
        cache: "no-cache",
      });
      if (response.ok) {
        return faceMeshUrl;
      }
    }
  } catch (e) {
    console.log(`panoramicNerveUrl error: ${e}`);
  }
}

function highlightItem(item) {
  if (vueApp.isHidden || !vueApp.isHighlightEnabled) return;

  if (!vueApp.getFocusedTooth()) {
    vueApp.allTeeth.forEach(function (model) {
      unhighlightItem(model);
    });
    if (item && item.isVisible && !item.isHidden && !item.extracted) {
      setHighlightedToothColor(item);
      if (vueApp.isDicomOpen) {
        highlightOutlines(item);
      }
    } else unhighlightItem(null);
  }
}

function unhighlightItem(item) {
  if (vueApp.isHidden || !vueApp.isHighlightEnabled) return;

  if (!vueApp.getFocusedTooth()) {
    if (item && item.isVisible && !item.isHidden && !item.extracted) {
      let color = vueApp.getActualColor(item);
      viewer.set_color(item.id, color);
    } else if (vueApp.isDicomOpen) {
      highlightOutlines(null);
    }
  }
}

function focusItem(item) {
  vueApp.allTeeth.forEach(function (model) {
    if (model != item && model.isVisible) {
      model.isFocused = false;
      model.isHidden = true;
    }
  });

  if (item) {
    item.isVisible = item.isFocused = true;
    item.isHidden = false;

    focusTooth(item);
  }
}

function unfocusItem() {
  vueApp.allTeeth.forEach(function (model) {
    model.isFocused = false;
    model.isHidden = false;
  });

  unfocusTooth(false);
}

function deleteItem(item) {
  console.log("deleteItem", item);

  if (vueApp.isViewMode) {
    swal({
      text: "Cannot delete in the view mode.",
    });
    return;
  }

  if (item && item.isTooth && !item.isDeleted) {
    swal({
      text: "Are you sure you want to delete this tooth?",
      buttons: {
        yes: { text: "Yes", value: true },
        no: { text: "No", value: false },
      },
    }).then((willDelete) => {
      if (willDelete) {
        if (item.isFocused) {
          unfocusItem();
        }

        item.isDeleted = true;
        var mesh = viewer.get_model_mesh(item.id);
        if (mesh) {
          viewer.remove_model(item.id);
        }
        vueApp.removeItem(item);
        vueApp.$emit("visibilityChanged", item);

        sendRemoveTeethRequest([item]);
      }
    });
  }
}

function editItemSettings(item) {
  if (item && !item.isDeleted && item.isTooth) {
    initToothNumberControls();

    // ToothNumberMode.setChangingTooth(item);
    showToothNumberDialog(item, true);
  }
}

function switchTeethRoots(value) {
  console.log("switchTeethRoots", value);
  vueApp.showTeethRoots = value;
  if (!isAnchorDataInitialized)
    showLoader(`Data calculation... Please wait`, "", true);

  setTimeout(() => {
    generateRootCrownMeshes();
    hideLoader();
    vueApp.allTeeth.forEach((model) => {
      let mesh = viewer.get_model_mesh(model.id);
      if (mesh && mesh.rootCrownMaterials && mesh.originalMaterial) {
        mesh.material = vueApp.showTeethRoots
          ? mesh.rootCrownMaterials
          : mesh.originalMaterial;
      }
    });
  }, 50);
}

async function switchBonesType(bonesType) {
  console.log("switchBonesType", bonesType);
  if (bonesType && vueApp.bonesType != bonesType) {
    vueApp.bonesType = bonesType;

    for (const item of vueApp.bones) {
      let mesh = viewer.get_model_mesh(item.id);
      let geometry = mesh.defaultGeometry
        ? mesh.defaultGeometry
        : mesh.geometry;
      let position = mesh.defaultPosition
        ? mesh.defaultPosition
        : mesh.position;

      if (bonesType == SOLID_BONES_TYPE) {
        geometry = mesh.solidGeometry;
        if (!mesh.solidPosition) {
          let box = offsetBox(
            mesh.solidGeometry.baseBox,
            -defaultXOffset,
            -defaultYOffset,
            -defaultZOffset,
          );
          mesh.solidPosition = new THREE.Vector3(
            box.center.x,
            box.center.y,
            box.center.z,
          );
        }
        position = mesh.solidPosition;
      }

      if (bonesType == FULL_BONES_TYPE && vueApp.hasFullBones) {
        if (!mesh.fullGeometry) {
          mesh.fullGeometry = await getFullBoneGeometry(item.isMaxilla);
          if (!mesh.fullGeometry) {
            mesh.fullGeometry = mesh.defaultGeometry;
            mesh.fullPosition = mesh.defaultPosition;
          }
        }

        geometry = mesh.fullGeometry;

        if (!mesh.fullPosition) {
          let box = offsetBox(
            mesh.fullGeometry.baseBox,
            -defaultXOffset,
            -defaultYOffset,
            -defaultZOffset,
          );
          mesh.fullPosition = new THREE.Vector3(
            box.center.x,
            box.center.y,
            box.center.z,
          );
        }
        position = mesh.fullPosition;
      }

      if (geometry && position) {
        mesh.geometry = geometry;
        mesh.position.set(position.x, position.y, position.z);
      }
    }

    vueApp.$emit("bonesTypeChanged", vueApp.bonesType);
  }
}

async function getFullBoneGeometry(isMaxilla) {
  let fullGeometry = null;
  showLoader(`Preparing data`, `Please wait...`, true);
  try {
    let stlFile = isMaxilla ? "maxilla_full.stl" : "mandibula_full.stl";
    let stlUrl = `${vueApp.stlBaseUrl}/${stlFile}`;
    let response = await fetch(stlUrl);
    if (response.status === 200) {
      const buffer = await response.arrayBuffer();
      // const bytes = new Uint8Array(buffer);
      fullGeometry = getGeometryFromStl(buffer);

      let matrix2 = new THREE.Matrix4();
      matrix2.makeRotationAxis(new THREE.Vector3(0, 1, 0), rotateY);
      fullGeometry.applyMatrix4(matrix2);

      let matrix1 = new THREE.Matrix4();
      matrix1.makeRotationAxis(new THREE.Vector3(1, 0, 0), rotateX);
      fullGeometry.applyMatrix4(matrix1);

      fullGeometry = new THREE.Geometry().fromBufferGeometry(fullGeometry);
      // cgeometry.mergeVertices();
      fullGeometry.computeVertexNormals();
      fullGeometry = new THREE.BufferGeometry().fromGeometry(fullGeometry);
      fullGeometry.computeBoundingBox();

      let box = offsetBox(fullGeometry.boundingBox);
      fullGeometry.baseBox = box;
      fullGeometry.center();
    }
  } catch (error) {
    console.log("getFullBoneGeometry error", error);
  }
  hideLoader();
  return fullGeometry;
}

function smoothToggle(item, value) {
  if (item) {
    item.isSmoothed = value;
    console.log("smoothToggle", item.isSmoothed);
    var mesh = viewer.get_model_mesh(item.id);
    if (mesh && mesh.smoothedGeometry) {
      mesh.geometry = item.isSmoothed
        ? mesh.smoothedGeometry
        : mesh.baseGeometry;
    }
  }
}

export {
  DEFAULT_BONES_TYPE,
  SOLID_BONES_TYPE,
  FULL_BONES_TYPE,
  fillTeethList,
  highlightItem,
  unhighlightItem,
  focusItem,
  unfocusItem,
  deleteItem,
  editItemSettings,
  switchTeethRoots,
  snapMandibleToOcclusion,
  fetchMandibleTransform,
  mandibleTransform,
  mandibleTransformInverted,
  switchBonesType,
  smoothToggle,
  switchFaceVisibility,
  switchFaceMaterial,
  switchFaceGeometry,
  faceItem,
  faceMesh
};
